import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Add, Danger, InfoCircle, TickCircle, Warning2 } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import { Portal } from '../internal/Portal';
import {
  ToastContext,
  type ToastApi,
  type ToastOptions,
  type ToastRecord,
  type ToastTone,
} from './toastContext';
import styles from './Toast.module.css';

export interface ToastProviderProps {
  children: ReactNode;
  /** Maximum toasts on screen; extra toasts wait until earlier ones close. */
  maxVisible?: number;
  /** Default auto-dismiss delay (ms) for non-error toasts. */
  defaultDuration?: number;
}

const ICONS: Record<ToastTone, typeof InfoCircle> = {
  info: InfoCircle,
  success: TickCircle,
  warning: Warning2,
  error: Danger,
};

/**
 * Provides useToast(). Toasts stack bottom-right; errors are announced assertively
 * (role="alert"), everything else politely (role="status").
 */
export function ToastProvider({
  children,
  maxVisible = 3,
  defaultDuration = 5000,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id?: string) => {
    setToasts((current) => (id ? current.filter((t) => t.id !== id) : []));
  }, []);

  const show = useCallback(
    (tone: ToastTone, title: ReactNode, options: ToastOptions = {}) => {
      counter.current += 1;
      const id = options.id ?? `toast-${counter.current}`;
      const duration =
        options.duration !== undefined
          ? options.duration
          : tone === 'error'
            ? null
            : defaultDuration;
      const record: ToastRecord = { ...options, id, tone, title, duration };
      setToasts((current) => [...current.filter((t) => t.id !== id), record]);
      return id;
    },
    [defaultDuration],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title, options) => show('success', title, options),
      error: (title, options) => show('error', title, options),
      info: (title, options) => show('info', title, options),
      warning: (title, options) => show('warning', title, options),
      dismiss,
    }),
    [show, dismiss],
  );

  const visible = toasts.slice(0, maxVisible);
  const assertive = visible.filter((t) => t.tone === 'error');
  const polite = visible.filter((t) => t.tone !== 'error');

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Portal>
        <div className={styles.viewport}>
          <div role="alert" aria-live="assertive" className={styles.region}>
            {assertive.map((toast) => (
              <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
            ))}
          </div>
          <div role="status" aria-live="polite" className={styles.region}>
            {polite.map((toast) => (
              <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
            ))}
          </div>
        </div>
      </Portal>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: string) => void }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const duration = toast.duration ?? null;
  const remaining = useRef(duration ?? 0);
  const expire = useEffectEvent(() => onDismiss(toast.id));
  const paused = hovered || focused;
  const Icon = ICONS[toast.tone];

  useEffect(() => {
    if (paused || duration === null) return;
    const startedAt = Date.now();
    const timer = window.setTimeout(() => expire(), Math.max(0, remaining.current));
    return () => {
      window.clearTimeout(timer);
      remaining.current -= Date.now() - startedAt;
    };
  }, [paused, duration]);

  return (
    <div
      className={cx(styles.toast, styles[toast.tone])}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      <span className={styles.icon} aria-hidden="true">
        <Icon size={20} color="currentColor" variant="Bold" />
      </span>
      <div className={styles.content}>
        <p className={styles.title}>{toast.title}</p>
        {toast.description && <div className={styles.description}>{toast.description}</div>}
        {toast.action && (
          <button
            type="button"
            className={styles.action}
            onClick={() => {
              toast.action?.onClick();
              onDismiss(toast.id);
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        className={styles.close}
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
      >
        <Add size={18} color="currentColor" className={styles.closeIcon} aria-hidden="true" />
      </button>
    </div>
  );
}
